import React, { useEffect, useMemo, useRef, useState } from "react";
import { History as HistoryIcon, Lock as LockIcon } from "lucide-react";
import { BandChip } from "@/components/PillarHeatMapV2";
import { ScoringVersionNote, mixedVersions } from "@/components/ScoringVersionNote";

const TEAL = "#007a9e", PURPLE = "#6b3fa0";
const r1 = (v) => (v == null ? null : Math.round(v * 10) / 10);
const f1 = (v) => (v == null ? "—" : r1(v).toFixed(1));
const sg = (v) => (v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(r1(v)).toFixed(1)}`);
const band = (v) => (v == null ? "" : v >= 80 ? "Healthy" : v >= 60 ? "Caution" : "High Risk");
const mdY = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : null);
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const gapOf = (h) => (h.overall_score != null && h.portfolio_score != null ? h.portfolio_score - h.overall_score : null);

// "School +4.9 (79.4 → 84.3) · Your assessment −3.3 (70.3 → 67.0) · Gap widened 9.1 → 17.3 (you rate lower)"
export const historySummary = (pts) => {
  if (pts.length < 2) return null;
  const a = pts[0], b = pts[pts.length - 1];
  const parts = [`School ${sg(b.overall_score - a.overall_score)} (${f1(a.overall_score)} → ${f1(b.overall_score)})`];
  const pa = pts.find((p) => p.portfolio_score != null), pb = [...pts].reverse().find((p) => p.portfolio_score != null);
  if (pa && pb && pa !== pb) parts.push(`Your assessment ${sg(pb.portfolio_score - pa.portfolio_score)} (${f1(pa.portfolio_score)} → ${f1(pb.portfolio_score)})`);
  const ga = gapOf(pa || {}), gb = gapOf(pb || {});
  if (ga != null && gb != null && pa !== pb) {
    parts.push(`Gap ${Math.abs(gb) >= Math.abs(ga) ? "widened" : "narrowed"} ${f1(Math.abs(ga))} → ${f1(Math.abs(gb))} (you rate ${gb < 0 ? "lower" : "higher"})`);
  }
  return parts.join(" · ");
};

const chartMarkup = (pts, showS, showP, W, H) => {
  const padL = 44, padR = W <= 560 ? 24 : 120, padT = 28, padB = 34, iw = W - padL - padR, ih = H - padT - padB;
  const n = pts.length;
  const x = (i) => padL + (n === 1 ? iw / 2 : 30 + (i / (n - 1)) * (iw - 60));
  const y = (v) => padT + ih - (v / 100) * ih;
  const gapX = n > 1 ? (iw - 60) / (n - 1) : iw;
  const every = Math.max(1, Math.ceil(46 / gapX));
  const maxCh = Math.max(4, Math.floor(Math.min(gapX * every, 220) / 7));
  const cut = (t) => (t.length > maxCh ? `${t.slice(0, maxCh - 1)}…` : t);
  const showVals = gapX >= 34;
  const o = [];
  o.push(`<rect x="${padL}" y="${y(100)}" width="${iw}" height="${y(80) - y(100)}" fill="#eef8f1"/>`);
  o.push(`<rect x="${padL}" y="${y(80)}" width="${iw}" height="${y(60) - y(80)}" fill="#fdf6e3"/>`);
  o.push(`<rect x="${padL}" y="${y(60)}" width="${iw}" height="${y(0) - y(60)}" fill="#fdf0ee"/>`);
  [["Healthy", 100], ["Caution", 80], ["High Risk", 60]].forEach(([t, v]) => o.push(`<text x="${padL + 6}" y="${y(v) + 13}" font-size="11" fill="#4b5563">${t}</text>`));
  [60, 80].forEach((v) => o.push(`<line x1="${padL}" x2="${padL + iw}" y1="${y(v)}" y2="${y(v)}" stroke="#6b7280" stroke-dasharray="3 3" stroke-width="0.8"/>`));
  [0, 20, 40, 60, 80, 100].forEach((v) => o.push(`<text x="${padL - 8}" y="${y(v) + 4}" font-size="11" fill="#374151" text-anchor="end">${v}</text>`));
  pts.forEach((p, i) => { if (i % every === 0 || i === n - 1) o.push(`<text x="${x(i)}" y="${H - 10}" font-size="12" font-weight="700" fill="#111827" text-anchor="middle">${esc(cut(String(p.period_label || "")))}</text>`); });
  if (showS && showP) {
    for (let i = 0; i < n - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      if (a.portfolio_score == null || b.portfolio_score == null) continue;
      o.push(`<polygon points="${x(i)},${y(a.overall_score)} ${x(i + 1)},${y(b.overall_score)} ${x(i + 1)},${y(b.portfolio_score)} ${x(i)},${y(a.portfolio_score)}" fill="${PURPLE}" fill-opacity="0.1"/>`);
    }
  }
  if (showS) {
    if (n > 1) o.push(`<path d="${pts.map((p, i) => `${i ? "L" : "M"}${x(i)} ${y(p.overall_score)}`).join(" ")}" fill="none" stroke="${TEAL}" stroke-width="3"/>`);
    pts.forEach((p, i) => {
      o.push(`<circle cx="${x(i)}" cy="${y(p.overall_score)}" r="6" fill="${TEAL}" stroke="#fff" stroke-width="2"/>`);
      if (showVals || i === n - 1) o.push(`<text x="${x(i)}" y="${y(p.overall_score) - 12}" font-size="12.5" font-weight="700" fill="#00607d" text-anchor="middle">${f1(p.overall_score)}</text>`);
    });
    o.push(`<text class="max-[560px]:hidden" x="${x(n - 1) + 14}" y="${y(pts[n - 1].overall_score) + 4}" font-size="12" font-weight="700" fill="#00607d">School</text>`);
  }
  if (showP) {
    for (let i = 0; i < n - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      if (a.portfolio_score != null && b.portfolio_score != null) o.push(`<line x1="${x(i)}" y1="${y(a.portfolio_score)}" x2="${x(i + 1)}" y2="${y(b.portfolio_score)}" stroke="${PURPLE}" stroke-width="3" stroke-dasharray="7 5"/>`);
    }
    pts.forEach((p, i) => {
      if (p.portfolio_score == null) { if (gapX >= 84 || n === 1) o.push(`<text x="${x(i)}" y="${y(30)}" font-size="11.5" font-style="italic" fill="${PURPLE}" text-anchor="middle">not assessed</text>`); return; }
      const cx = x(i), cy = y(p.portfolio_score);
      o.push(`<path d="M${cx} ${cy - 7}L${cx + 7} ${cy}L${cx} ${cy + 7}L${cx - 7} ${cy}Z" fill="${PURPLE}" stroke="#fff" stroke-width="2"/>`);
      if (showVals || i === n - 1) o.push(`<text x="${cx}" y="${cy + 21}" font-size="12.5" font-weight="700" fill="${PURPLE}" text-anchor="middle">${f1(p.portfolio_score)}</text>`);
    });
    const last = [...pts].reverse().findIndex((p) => p.portfolio_score != null);
    if (last >= 0) { const li = n - 1 - last; o.push(`<text class="max-[560px]:hidden" x="${x(li) + 14}" y="${y(pts[li].portfolio_score) + 4}" font-size="12" font-weight="700" fill="${PURPLE}">Your assessment</text>`); }
  }
  if (showS && showP) {
    pts.forEach((p, i) => {
      const g = gapOf(p);
      if (g == null) return;
      const my = (y(p.overall_score) + y(p.portfolio_score)) / 2, t = sg(g), w = t.length * 7.5 + 14;
      const px = i === n - 1 && n > 1 ? x(i) - 12 - w : Math.min(x(i) + 12, W - w - 2);
      o.push(`<g data-testid="history-gap-pill-${esc(p.id)}"><rect x="${px}" y="${my - 10}" width="${w}" height="20" rx="10" fill="#fff" stroke="${PURPLE}"/><text x="${px + w / 2}" y="${my + 4}" font-size="11.5" font-weight="700" fill="${PURPLE}" text-anchor="middle">${t}</text></g>`);
    });
  }
  return { markup: o.join(""), xPct: (i) => (x(i) / W) * 100 };
};

const Tip = ({ p, viewer }) => (
  <div role="tooltip" className="pointer-events-none rounded-md bg-[#111827] px-3 py-2 text-[12.5px] text-white shadow-lg">
    <div className="font-bold">{p.period_label}</div>
    <div>School: {f1(p.overall_score)} · {band(p.overall_score)}</div>
    {viewer === "portfolio" && <div>Your assessment: {p.portfolio_score != null ? `${f1(p.portfolio_score)} · ${band(p.portfolio_score)}` : "Not assessed"}</div>}
    {viewer === "portfolio" && gapOf(p) != null && <div>Gap: {sg(gapOf(p))}</div>}
  </div>
);

const ScoreCell = ({ v, label }) => (
  <div className="flex items-center gap-2" aria-label={label}>
    {v != null ? <><span className="text-[15px] font-bold text-[#111827]">{f1(v)}</span><BandChip score={v} completion={100} /></> : <span className="text-[13px] italic text-[#6b3fa0]">Not assessed</span>}
  </div>
);

// Diagnostic History v2: bigger banded chart + period list. viewer="school" → school line only.
export const DiagnosticHistoryV2 = ({ history, viewer = "school", ids, compareHref, actions, headerRight }) => {
  const pts = useMemo(() => (history || []).filter((h) => (h.status === "approved" || h.locked) && h.overall_score != null), [history]);
  const [mode, setMode] = useState("both");
  const [tip, setTip] = useState(null);
  const box = useRef(null);
  const [W, setW] = useState(760);
  useEffect(() => {
    if (!box.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, [pts.length]);
  if (!pts.length) return null;
  const H = W <= 560 ? 280 : 340;
  const isPf = viewer === "portfolio";
  const showS = !isPf || mode !== "portfolio", showP = isPf && mode !== "school";
  const { markup, xPct } = chartMarkup(pts, showS, showP, W, H);
  const summary = isPf ? historySummary(pts) : null;
  const seg = (on) => `min-h-[44px] px-4 text-[13px] font-semibold ${on ? "bg-[#111827] text-white" : "bg-white text-[#111827] hover:bg-[#f3f4f6]"}`;
  const grid = isPf ? "min-[761px]:grid-cols-[minmax(0,1.6fr)_1fr_1fr_80px_90px]" : "min-[761px]:grid-cols-[minmax(0,1.6fr)_1fr_auto]";
  return (
    <div data-testid={ids.section}>
      <div className="rounded-2xl border border-[#e5e7eb] bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-bold text-[#111827]"><HistoryIcon className="h-5 w-5" /> Diagnostic History</h2>
            {summary && <p className="mt-1 text-[14px] text-[#374151]" data-testid="history-summary">{summary}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-[#374151]" data-testid="history-legend">
              <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-5 bg-[#007a9e]" />● School's diagnostic</span>
              {isPf && <span className="inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed border-[#6b3fa0]" />◆ Your assessment</span>}
              {isPf && <span className="inline-flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-[#6b3fa0]/15" />Gap</span>}
              <span>Bands: Healthy ≥80 · Caution 60–79 · High Risk &lt;60</span>
            </div>
            {mixedVersions(pts.flatMap((h) => [h.scoring_version, h.portfolio_score != null ? h.portfolio_scoring_version : null])) && <ScoringVersionNote testId="history-scoring-version-note" />}
          </div>
          <div className="flex items-center gap-2">
            {isPf && (
              <div className="inline-flex overflow-hidden rounded-md border border-[#d1d5db]" role="group" aria-label="Lines shown">
                {[["school", "School"], ["portfolio", "Portfolio"], ["both", "Both"]].map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={mode === k} onClick={() => setMode(k)} className={seg(mode === k)} data-testid={`history-toggle-${k}`}>{l}</button>
                ))}
              </div>
            )}
            {headerRight}
          </div>
        </div>
        <div ref={box} className="relative mt-3" onMouseLeave={() => setTip(null)}>
          <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={`Diagnostic History chart${summary ? `: ${summary}` : ""}. The period list below has the same data.`}
            className="block max-w-full" data-testid={ids.chart} dangerouslySetInnerHTML={{ __html: markup }} />
          {pts.map((p, i) => (
            <button key={p.id} type="button" aria-label={`${p.period_label}: school ${f1(p.overall_score)}${isPf ? `, your assessment ${p.portfolio_score != null ? f1(p.portfolio_score) : "not assessed"}` : ""}`}
              className="absolute top-0 h-[300px] w-10 -translate-x-1/2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#007a9e] max-[560px]:h-[250px]"
              style={{ left: `${xPct(i)}%` }} onMouseEnter={() => setTip(i)} onFocus={() => setTip(i)} onBlur={() => setTip(null)} data-testid={`history-point-${p.id}`} />
          ))}
          {tip != null && pts[tip] && (
            <div className="absolute top-2 z-10 -translate-x-1/2" style={{ left: `${Math.min(85, Math.max(15, xPct(tip)))}%` }} data-testid="history-tooltip"><Tip p={pts[tip]} viewer={viewer} /></div>
          )}
        </div>
        <div className="mt-4 border-t border-[#e5e7eb]" data-testid={ids.list}>
          <div className={`hidden gap-3 px-1 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#4b5563] min-[761px]:grid ${grid}`}>
            <span>Period</span><span>School's diagnostic</span>{isPf && <><span>Your assessment</span><span>Gap</span></>}<span />
          </div>
          {[...pts].reverse().map((h) => {
            const g = gapOf(h);
            const dates = [h.approved_at && `Approved ${mdY(h.approved_at)}`, h.completed_at && `Completed ${mdY(h.completed_at)}`].filter(Boolean).join(" · ");
            return (
              <div key={h.id} className={`grid grid-cols-1 gap-2 border-t border-[#f3f4f6] px-1 py-3 min-[761px]:items-center min-[761px]:gap-3 ${grid}`} data-testid={ids.row(h)}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-[#111827]">{h.period_label}
                    {h.locked && <span className="inline-flex items-center gap-1 rounded border border-[#a7f3d0] bg-[#ecfdf5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#065f46]"><LockIcon className="h-3 w-3" /> Locked</span>}
                  </div>
                  <div className="text-[12.5px] text-[#4b5563]">{dates || (h.status === "approved" ? "Approved" : "")}</div>
                </div>
                <div><span className="text-[11px] font-semibold uppercase text-[#4b5563] min-[761px]:hidden">School's diagnostic </span><ScoreCell v={h.overall_score} /></div>
                {isPf && <div><span className="text-[11px] font-semibold uppercase text-[#4b5563] min-[761px]:hidden">Your assessment </span><ScoreCell v={h.portfolio_score} /></div>}
                {isPf && <div className="text-[15px] font-bold text-[#6b3fa0]" data-testid={`history-gap-${h.id}`}><span className="text-[11px] font-semibold uppercase text-[#4b5563] min-[761px]:hidden">Gap </span>{sg(g)}</div>}
                <div className="flex items-center gap-3 min-[761px]:justify-end">
                  {isPf && compareHref?.(h) && <a href={compareHref(h)} className="inline-flex min-h-[44px] items-center text-[13px] font-semibold text-[#6b3fa0] underline" data-testid={`history-compare-${h.id}`}>Compare →</a>}
                  {actions?.(h)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticHistoryV2;
